import moment from "moment";

import { buildViewsTimeline } from "../charts/charts.utils";
import { getCountryCode, getCountryLabel } from "../charts/countries.utils";

import type {
  GetContentCountriesResponse,
  GetContentViewsGraphResponse,
  GetDevicesUsedByContentResponse,
  LiveViewer,
} from "@/src/api/StatsAPI.types";
import type { CountriesState } from "@/src/reducers/fetch.types";

import type {
  LiveTimeDomainParams,
  LiveViewerPoint,
  LiveViewerSummary,
} from "./contentItem.types";
import type {
  RankedDatum,
  TimelineDatum,
  ViewsSeriesKey,
} from "../charts/charts.types";

const ON_AIR_STATUS_LIST = ["ongoing", "live"];
const MIN_LIVE_DOMAIN_MS = 60_000;

export function isLiveOnAir(status: string | undefined): boolean {
  return !!status && ON_AIR_STATUS_LIST.includes(status);
}

export function buildContentViewsGraphTimeline(
  response: Pick<GetContentViewsGraphResponse, "data"> | undefined,
): TimelineDatum<ViewsSeriesKey>[] {
  return buildViewsTimeline(response?.data ?? []);
}

export function buildContentDevicesRanked(
  response: Pick<GetDevicesUsedByContentResponse, "data"> | undefined,
  getPlatformLabel: (platform: string) => string,
): RankedDatum<ViewsSeriesKey>[] {
  return Object.entries(response?.data[0] ?? {}).flatMap(([platform, stat]) =>
    stat.views > 0
      ? [{ label: getPlatformLabel(platform), values: { views: stat.views } }]
      : [],
  );
}

export function buildContentCountriesMapData(
  response: Pick<GetContentCountriesResponse, "data"> | undefined,
  countries: Pick<CountriesState, "data" | "indexes">,
): Record<string, number> {
  const data: Record<string, number> = {};
  Object.entries(response?.data[0] ?? {}).forEach(([countryUuid, stat]) => {
    const country = getCountryCode(countries, countryUuid);
    if (country) data[country] = stat.views;
  });
  return data;
}

export function buildContentCountriesRankedList(
  response: Pick<GetContentCountriesResponse, "data"> | undefined,
  countries: Pick<CountriesState, "data" | "indexes">,
  lang: string,
): RankedDatum<ViewsSeriesKey>[] {
  return Object.entries(
    buildContentCountriesMapData(response, countries),
  ).flatMap(([code, views]) =>
    views > 0
      ? [{ label: getCountryLabel(code, lang), values: { views } }]
      : [],
  );
}

export function buildLiveViewerPointList(
  viewerList: readonly LiveViewer[],
): LiveViewerPoint[] {
  const pointByDatetime = new Map(
    viewerList.map((viewer) => [
      viewer.datetime,
      {
        timestamp: moment.utc(viewer.datetime).valueOf(),
        viewers: viewer.viewers,
        cumulative: viewer.cumulative,
      },
    ]),
  );
  return [...pointByDatetime.values()].sort(
    (pointA, pointB) => pointA.timestamp - pointB.timestamp,
  );
}

export function getLiveViewerSummary(
  pointList: readonly LiveViewerPoint[],
): LiveViewerSummary {
  const lastPoint = pointList.at(-1);
  if (!lastPoint) return { current: null, peak: null, unique: null };
  return {
    current: lastPoint.viewers,
    peak: pointList.reduce((peak, point) => Math.max(peak, point.viewers), 0),
    unique: lastPoint.cumulative,
  };
}

function getLiveEnd({
  liveEnd,
  isOnAir,
  now,
  fallback,
}: {
  liveEnd: string | null | undefined;
  isOnAir: boolean;
  now: number;
  fallback: number;
}): number {
  if (liveEnd) return moment.utc(liveEnd).valueOf();
  return isOnAir ? now : fallback;
}

export function getLiveTimeDomain({
  beginDate,
  liveEnd,
  pointList,
  isOnAir,
  now,
}: LiveTimeDomainParams): [number, number] {
  const start = beginDate
    ? moment.utc(beginDate).valueOf()
    : (pointList[0]?.timestamp ?? now);
  const end = getLiveEnd({
    liveEnd,
    isOnAir,
    now,
    fallback: pointList.at(-1)?.timestamp ?? start,
  });
  return [start, Math.max(end, start + MIN_LIVE_DOMAIN_MS)];
}
